/**
 * COMPARACIÓN W0 · W4 · W8 · W12.
 *
 * La vista que responde «¿estoy mejorando?» con datos y con audio, no con una
 * sensación. Se abre desde Progreso y no ocupa un destino de la navegación:
 * son exactamente 5 y nunca 6.
 *
 * Dos reglas duras:
 *
 * 1. Una medición que falta dice «Pendiente». NUNCA un cero. Un cero afirma
 *    que se midió y dio cero, que es lo contrario de no haber medido.
 * 2. El audio se carga cuando se pide, no al abrir. Cuatro blobs de audio en
 *    memoria por entrar a mirar unos números es justo lo que hace que Android
 *    mate la pestaña.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { AREAS, NOMBRE_AREA, type Area } from '../../domain/modelo/areas';
import type { Grabacion } from '../../domain/modelo/grabacion';
import {
  areasComparables,
  comparacionLongitudinal,
  type ColumnaMedicion,
} from '../../domain/reglas/checkpoints';
import { FUENTE_LISTENING_POR_CLAVE } from '../../curriculum/v1/fuentesListening';
import { grabacionesRepo } from '../../persistencia/grabacionesRepo';
import { sesionesRepo } from '../../persistencia/sesionesRepo';
import { Pantalla } from '../comunes/Pantalla';
import { Suave, Tarjeta } from '../comunes/Tarjeta';
import estilos from './Comparacion.module.css';

const PENDIENTE = '—';

/**
 * Reproductor que solo construye el Object URL cuando se pide.
 *
 * El Object URL nunca es almacenamiento: se crea desde el Blob persistido y
 * se revoca al desmontar.
 */
function Muestra({ grabacion }: { grabacion: Grabacion }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!url) return;
    return () => URL.revokeObjectURL(url);
  }, [url]);

  const blob = grabacion.blobLocal;
  if (!blob) {
    // Sin blob local no hay nada que reproducir. En la V1 el audio vive solo
    // en este dispositivo; la copia en la nube es Fase 8.
    return <span className={estilos.pendiente}>Audio no disponible en este dispositivo</span>;
  }

  if (!url) {
    return (
      <button
        type="button"
        className={estilos.escuchar}
        onClick={() => setUrl(URL.createObjectURL(blob))}
      >
        Escuchar {grabacion.nombre}
      </button>
    );
  }

  // Sin `autoPlay`: comparar es una decisión, no algo que deba empezar solo.
  return <audio className={estilos.audio} src={url} controls preload="none" />;
}

function celdaAutoevaluacion(columna: ColumnaMedicion, area: Area): string {
  const valor = columna.autoevaluaciones[area];
  return typeof valor === 'number' ? `${valor}/5` : PENDIENTE;
}

export function Comparacion() {
  const sesiones = useLiveQuery(() => sesionesRepo.listar(), [], undefined);
  const grabaciones = useLiveQuery(() => grabacionesRepo.listar(), [], undefined);

  if (!sesiones || !grabaciones) {
    return (
      <Pantalla titulo="Comparación">
        <Suave>Cargando…</Suave>
      </Pantalla>
    );
  }

  const columnas = comparacionLongitudinal(sesiones, grabaciones);
  const hechas = columnas.filter((columna) => columna.sesion !== null);
  const comparables = areasComparables(columnas, AREAS);

  return (
    <Pantalla titulo="Comparación" tituloVisible>
      <Link className={estilos.volver} to="/progreso">
        ← Progreso
      </Link>

      {hechas.length === 0 ? (
        <Tarjeta>
          <Suave>
            Todavía no hay ninguna medición. La primera es el baseline; después vienen
            los checkpoints de las semanas 4, 8 y 12.
          </Suave>
        </Tarjeta>
      ) : null}

      {hechas.length === 1 ? (
        <Suave>
          Con una sola medición no hay comparación todavía, solo un punto de partida. La
          siguiente es el checkpoint de la semana 4.
        </Suave>
      ) : null}

      {/* ── Estado de cada medición ─────────────────────────────────── */}
      <Tarjeta titulo="Las cuatro mediciones">
        <ul className={estilos.mediciones}>
          {columnas.map((columna) => (
            <li key={columna.semana} className={estilos.medicion}>
              <span className={estilos.etiqueta}>{columna.etiqueta}</span>
              {columna.sesion ? (
                <span className={estilos.fecha}>
                  {new Date(columna.sesion.iniciadaEn).toLocaleDateString('es')}
                </span>
              ) : (
                <span className={estilos.pendiente}>Pendiente</span>
              )}
            </li>
          ))}
        </ul>
      </Tarjeta>

      {/* ── Autoevaluaciones ────────────────────────────────────────── */}
      {hechas.length > 0 ? (
        <Tarjeta titulo="Cómo lo sentiste">
          <Suave>
            Tu percepción en cada medición, de 1 a 5. Un guion significa que esa
            medición no evaluó esa área, no que diera cero.
          </Suave>
          <div className={estilos.contenedorTabla}>
            <table className={estilos.tabla}>
              <thead>
                <tr>
                  <th scope="col">Área</th>
                  {columnas.map((columna) => (
                    <th key={columna.semana} scope="col">
                      {columna.semana === 0 ? 'W0' : `W${columna.semana}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {AREAS.map((area) => (
                  <tr key={area}>
                    <th scope="row">{NOMBRE_AREA[area]}</th>
                    {columnas.map((columna) => (
                      <td key={columna.semana}>{celdaAutoevaluacion(columna, area)}</td>
                    ))}
                  </tr>
                ))}
                <tr className={estilos.filaConfianza}>
                  <th scope="row">Confianza</th>
                  {columnas.map((columna) => (
                    <td key={columna.semana}>
                      {columna.confianza === null ? PENDIENTE : `${columna.confianza}/5`}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          {comparables.length === 0 ? (
            <Suave>
              Todavía no hay ningún área con datos en dos mediciones: hasta entonces son
              valores sueltos, no una evolución.
            </Suave>
          ) : (
            <Suave>
              Áreas con datos en dos mediciones o más, que son las únicas comparables:{' '}
              {comparables.map((area) => NOMBRE_AREA[area]).join(', ')}.
            </Suave>
          )}
        </Tarjeta>
      ) : null}

      {/* ── Audio ───────────────────────────────────────────────────── */}
      {hechas.some((columna) => columna.grabaciones.length > 0) ? (
        <Tarjeta titulo="Escuchar la evolución">
          <Suave>
            Es la comparación que más dice. El audio se carga cuando lo pedís, no al
            abrir esta pantalla.
          </Suave>
          <ul className={estilos.audios}>
            {hechas.map((columna) => (
              <li key={columna.semana} className={estilos.bloqueAudio}>
                <span className={estilos.etiqueta}>{columna.etiqueta}</span>
                {columna.grabaciones.length === 0 ? (
                  <span className={estilos.pendiente}>Sin muestra</span>
                ) : (
                  columna.grabaciones.map((grabacion) => (
                    <Muestra key={grabacion.id} grabacion={grabacion} />
                  ))
                )}
              </li>
            ))}
          </ul>
        </Tarjeta>
      ) : null}

      {/* ── Resúmenes y fuente usada ────────────────────────────────── */}
      {hechas.some((columna) => columna.resumenChatGPT) ? (
        <Tarjeta titulo="Resúmenes de ChatGPT">
          <Suave>
            Tal como los pegaste. La aplicación no los interpreta: están acá para que los
            leas uno al lado del otro.
          </Suave>
          {hechas
            .filter((columna) => columna.resumenChatGPT)
            .map((columna) => (
              <div key={columna.semana} className={estilos.resumen}>
                <span className={estilos.etiqueta}>{columna.etiqueta}</span>
                <p className={estilos.textoResumen}>{columna.resumenChatGPT}</p>
              </div>
            ))}
        </Tarjeta>
      ) : null}

      {hechas.some((columna) => columna.fuenteListeningClave) ? (
        <Tarjeta titulo="Material de listening usado">
          <Suave>
            Cada medición usa una fuente distinta a propósito: si ya conocés el material,
            la prueba mide memoria en vez de comprensión.
          </Suave>
          <ul className={estilos.mediciones}>
            {hechas
              .filter((columna) => columna.fuenteListeningClave)
              .map((columna) => (
                <li key={columna.semana} className={estilos.medicion}>
                  <span className={estilos.etiqueta}>{columna.etiqueta}</span>
                  <span className={estilos.fecha}>
                    {FUENTE_LISTENING_POR_CLAVE[columna.fuenteListeningClave!]?.nombre ??
                      columna.fuenteListeningClave}
                  </span>
                </li>
              ))}
          </ul>
        </Tarjeta>
      ) : null}
    </Pantalla>
  );
}
